import {
  Grid,
  GridItem,
  PendingContent,
  SearchInputField,
  Space,
  Spinner,
} from 'plume-ui';

import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MixPanelEvents } from '../../../mixPanelEvents';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { AvailableScreens } from '../../trackingAnalytics/types';

import FakeMonitorCell from './FakeMonitorCell';

import { generateDNSChartData, generateFakeChartData } from './utils';

const demoMonitors = [
  {
    name: 'DHCP assignment failures',
    id: 'dhcp',
    frequencyInHours: 0,
    fakeLocationCount: 1000,
    fakeGraphData: generateFakeChartData(24, 1000, 900),
    lastUpdateTime: new Date().toISOString(),
  },
  {
    name: 'DNS resolution failures',
    id: 'dns',
    frequencyInHours: 0,
    fakeLocationCount: 20311,
    fakeGraphData: generateDNSChartData(24),
    lastUpdateTime: new Date().toISOString(),
  },
  {
    name: 'OLT hardware health',
    id: 'olt',
    frequencyInHours: 0,
    fakeLocationCount: 0,
    fakeGraphData: generateFakeChartData(24, 4, 3),
    lastUpdateTime: new Date().toISOString(),
  },
  {
    name: 'ONT hardware health',
    id: 'ont',
    frequencyInHours: 0,
    fakeLocationCount: 0,
    fakeGraphData: generateFakeChartData(24, 4, 1),
  },
  {
    name: 'Configuration failures',
    id: 'conf',
    frequencyInHours: 1,
    fakeLocationCount: 5,
    fakeGraphData: generateFakeChartData(24, 5, 5),
  },
  {
    name: 'Port flapping',
    id: 'port',
    frequencyInHours: 1,
    fakeLocationCount: 0,
    fakeGraphData: generateFakeChartData(24, 3, 2),
  },
  {
    name: 'VoIP QoE',
    id: 'voip',
    frequencyInHours: 1,
    fakeLocationCount: 300,
    fakeGraphData: generateFakeChartData(24, 300, 250),
  },
  {
    name: 'Service outages',
    id: 'service',
    frequencyInHours: 1,
    fakeLocationCount: 0,
    fakeGraphData: generateFakeChartData(24, 4, 2),
  },
  {
    name: 'Last mile degradation',
    id: 'lastmile',
    frequencyInHours: 1,
    fakeLocationCount: 600,
    fakeGraphData: generateFakeChartData(24, 600, 500),
  },
];

const AccessNetworkMonitors = () => {
  const { t } = useTranslation();

  const trackEvent = useTrackEvent();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const filtered = useMemo(() => {
    if (!searchQuery) {
      return demoMonitors;
    }
    return demoMonitors?.filter((monitor) =>
      monitor.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [searchQuery, demoMonitors]);

  useEffect(() => {
    trackEvent({
      eventName: MixPanelEvents.SCREEN,
      additionalContent: {
        SCREEN: AvailableScreens.Monitors,
      },
    });
  }, []);

  const menuCallbackHandlers = {
    viewDetails: () => {},
  };

  return (
    <span className="UserMonitors">
      <Grid>
        <GridItem colSpan="12" tabletColSpan="12">
          <div className="UserMonitors__header">
            <div className="UserMonitors__headerLeft">
              <SearchInputField
                id={'monitorSearch'}
                rounded
                placeholder={t('monitors.searchMonitor.title')}
                onKeyUp={(event) =>
                  setSearchQuery((event.target as HTMLInputElement).value)
                }
                onChange={(event) =>
                  setSearchQuery((event.target as HTMLInputElement).value)
                }
              />
            </div>
            <div className="UserMonitors__headerRight"></div>
          </div>
        </GridItem>
      </Grid>
      <Space size="s" />
      <PendingContent loading={false} loader={Spinner} transparent>
        <Grid>
          {filtered ? (
            filtered.length > 0 ? (
              filtered.map((monitor) => (
                <GridItem
                  key={monitor.id || monitor.name}
                  colSpan="4"
                  tabletColSpan="3"
                >
                  <div className="UserMonitors__card">
                    <FakeMonitorCell
                      name={monitor.name}
                      id={monitor.id}
                      menuHandler={menuCallbackHandlers}
                      frequencyInHours={monitor.frequencyInHours}
                      fakeGraphData={monitor.fakeGraphData}
                      fakeLocationCount={monitor.fakeLocationCount}
                      lastUpdateTime={monitor.lastUpdateTime}
                      subscriberOutreach={{
                        id: '',
                        status: 'active',
                      }}
                      supportOutreach={{
                        id: '',
                        status: 'active',
                      }}
                      supportWebhook={{
                        id: '',
                        status: 'active',
                      }}
                    />
                  </div>
                </GridItem>
              ))
            ) : (
              <GridItem colSpan="12" tabletColSpan="12">
                <div className="UserMonitors__header__no-results">
                  {t('monitors.searchMonitor.noResults')}
                </div>
              </GridItem>
            )
          ) : null}
        </Grid>
      </PendingContent>
    </span>
  );
};

export default memo(AccessNetworkMonitors);
